import { StringUtility } from "@common/utils";
import { DateTime } from "luxon";
import { ILabOrderModel, IVisitConsentModel, IVisitNoteModel, LabOrderModel, VisitConsentModel, VisitNoteModel, IVisitAttachmentModel, VisitAttachmentModel, IIntakeModel, IntakeModel } from ".";
import { AppointmentTypeModel, IAppointmentTypeModel } from "./AppointmentTypeModel";
import { ClientModel, IClientModel } from "./ClientModel";
import { CommunicationType, ReservationAssignmentStatuses, VisitNoteType } from "./Enums";
import { IModel, Model } from "./Model";

import { IPetModel, PetModel } from "./PetModel";
import { IPhotoModel, PhotoModel } from "./PhotoModel";
import { IReservationModel, ReservationModel } from "./ReservationModel";
import { IStorageContentModel, StorageContentModel } from "./StorageContentModel";
import { IVisitCompletionTypeModel, VisitCompletionTypeModel } from "./VisitCompletionTypeModel";
import { IVisitDiagnosisModel, VisitDiagnosisModel } from "./VisitDiagnosisModel";
import { IVisitEventModel, VisitEventModel } from "./VisitEventModel";
import { IVisitExamResultModel, VisitExamResultModel } from "./VisitExamResultModel";
import { VisitSalesItemModel } from "./VisitSalesItemModel";
import { IVisitStatusModel, VisitStatusModel } from "./VisitStatusModel";
import { IVisitWellnessExamModel, VisitWellnessExamModel } from "./VisitWellnessExamModel";
import { IVisitSectionVersionModel, VisitSectionVersionModel } from "./VisitSectionVersionModel";
import { IVisitAnesthesiaFormModel, VisitAnesthesiaFormModel } from "./VisitAnesthesiaFormModel";
import { HospitalPrescriptionModel, IHospitalPrescriptionModel } from "./HospitalPrescriptionModel";
import { FollowUpAppointmentModel } from "@common/models/FollowUpAppointmentModel";
import { VisitReferralModel } from "@common/models/VisitReferralModel";


export interface IVisitModel extends IModel {
    visitId: number;
    visitPublicId: string | undefined;
    cashPayment: number;
    checkPayment: number;
    clinicId: number;
    clinicStoreId: number;
    consultationValidationStatusId: number;
    consultationHasBeenValidated: boolean;
    visitDate: string | undefined;
    fleaTickProductId: number | undefined;
    hasMicrochip: boolean | undefined;
    hasOptedOutOfPackage: boolean;
    hasOutsideContact: boolean | undefined;
    heartwormProductId: number | undefined;
    isExpress: boolean;
    isRxOnly: boolean;
    isPetNotPresent: boolean | undefined;
    isPetPregnant: boolean | undefined;
    lastVaccineDate: string | undefined;
    lastRabiesDate: string | undefined;
    paymentMethodId: number | undefined;
    petId: number;
    veterinarianSignaturePhotoId: number | undefined;
    vetServicesHasBeenValidated: boolean;
    vetServicesValidationStatusId: number;
    visitCompletionTypeId: number;
    visitStatusId: number;
    visitSummaryDocumentId: number | undefined;
    sMSStatus: number;
    hasVetAttestation: boolean;
    hasPetAttestation: boolean;
    room: string | undefined;
    visitWellnessExam: IVisitWellnessExamModel;
    visitDiagnoses: IVisitDiagnosisModel[];
    reasonForVisit: string | undefined;
    visitHistory: string;
    visitSubjective: string;
    normalResponses: string;
    abnormalResponses: string;
    unableToExamResponses: string;
    freeNonPackageSalesItems: VisitSalesItemModel[]; 
    addOnSalesItems: VisitSalesItemModel[]; 
    paidNonPackageSalesItems: VisitSalesItemModel[]; 
    discountAndRebateSalesItems: VisitSalesItemModel[];
    additionalInfoLabels: string[];
    // siblings: VisitSiblingModel[]; // TODO JFH: Not immediately needed. Leaving out for now to reduce scope of work.
    showRabiesDisclaimer: boolean;
    vetAssistantFirstLast: string;
    brandId: number;
    clinicStateId: number;
    clinicAllowChangingSingleVisitVet: boolean;
    marketId: number;
    firstVisitDate: string | undefined;
    lastVisitDate: string | undefined;
    checkinStart: string | undefined;
    consultationStart: string | undefined;
    vetServicesStart: string | undefined;
    checkoutStart: string | undefined;
    clientSignaturePhotoId: number | undefined;
    rabiesCertificateDocumentId: number | undefined;
    prescriptionDocumentId: number | undefined;
    partnerId: number;
    visitStatus: IVisitStatusModel;
    // consultationValidationStatus: ValidationStatusModel; // TODO JFH: Not immediately needed. Leaving out for now to reduce scope of work.
    // vetServicesValidationStatus: ValidationStatusModel; // TODO JFH: Not immediately needed. Leaving out for now to reduce scope of work.
    pet: IPetModel;
    client: IClientModel;
    visitEvent: IVisitEventModel;
    historicalVisitWellnessExams: IVisitWellnessExamModel[] | undefined;
    // paymentMethod: PaymentMethodModel; // TODO JFH: Not immediately needed. Leaving out for now to reduce scope of work.
    visitSalesItems: VisitSalesItemModel[];
    veterinarianSignaturePhoto: IPhotoModel | undefined;
    clinicVetSignaturePhoto: IPhotoModel | undefined;
    clientSignaturePhoto: IPhotoModel | undefined;
    visitSummaryDocument: IStorageContentModel | undefined;
    rabiesCertificateDocument: IStorageContentModel | undefined;
    prescriptionDocument: IStorageContentModel | undefined;
    // heartwormProduct: HeartwormProductModel; // TODO JFH: Not immediately needed. Leaving out for now to reduce scope of work.
    // fleaTickProduct: FleaTickProductModel; // TODO JFH: Not immediately needed. Leaving out for now to reduce scope of work.
    salesPackageId: number | undefined;
    salesPackagePrice: number | undefined;
    salesPackageName: string | undefined;
    salesPackageDiscountAmount: number | undefined;
    salesPackageInvoicePrice: number | undefined;
    salesPackageSalesSubCategoryId: number | undefined;
    salesPackageSalesSubCategoryName: string;
    salesPackageSpeciesId: number | undefined;
    salesPackageSpeciesName: string;
    salesPackageDisplayName: string;
    salesPackageDescription: string;
    salesPackageCode: string;
    salesPackageHelpPhotoId: number | undefined;
    salesPackageSortOrder: number | undefined;
    salesPackageExpirationDays: number | undefined;
    salesPackageVaccineReminderDefaultDays: number | undefined;
    isSalesPackageRefunded: boolean;
    visitCompletionType: IVisitCompletionTypeModel;
    serviceAmount: number;
    productAmount: number;
    licenseFeeAmount: number;
    vetcoPrescriptionAmount: number;
    discountAmount: number;
    taxPercentage: number;
    subtotalPreDiscountsAmount: number;
    subtotalPostDiscountsAmount: number;
    taxAmount: number;
    refundAmount: number;
    taxableTotalAmount: number;
    nonTaxableTotalAmount: number;
    totalAmount: number;
    servicesOnlyDiscountAmount: number;
    productsOnlyDiscountAmount: number;
    servicesRebateAmount: number | undefined;
    productsRebateAmount: number | undefined;
    totalRebateAmount: number | undefined;
    nonTaxableFeeAmount: number | undefined;
    taxableFeeAmount: number | undefined;
    totalFeeAmount: number | undefined;
    totalAOVAmount: number | undefined;
    veterinarianId: number | undefined;
    veterinarianFirstName: string;
    veterinarianLastName: string;
    veterinarianLicenseNumber: string;
    veterinarianLicenseStateId: number | undefined;
    veterinarianLicenseStateAbbreviation: string;
    veterinarianFullName: string;
    veterinarianLicenseExpirationDate: string | undefined;
    veterinarianEmail: string;
    dispensingAuthUserId: number | undefined;
    dispensingUserFirstName: string | undefined;
    dispensingUserLastName: string | undefined;
    thankYouNote: string;
    storeStateNote: string;
    clientStateNote: string;
    visitTagId: number | undefined;
    // visitTag: VisitTagModel; // TODO JFH: Not immediately needed. Leaving out for now to reduce scope of work.
    reservationId: number | undefined;
    reservation: IReservationModel | undefined;
    examResults: IVisitExamResultModel[] | undefined;
    visitNotes: IVisitNoteModel[] | undefined;
    referrals: VisitReferralModel[]; 
    hasEvaluatedAutoRecommendations: boolean;
    intakeId: number | undefined;
    intake: IIntakeModel;
    arrived: string | undefined;
    firstCheckIn: string | undefined;
    visitStarted: string | undefined;
    arrivedWaitingTime: number | undefined;
    checkInWaitingTime: number | undefined;
    intakeCompletionStatusId: number | undefined;
    // intakeCompletionStatus: IntakeCompletionStatusModel; // TODO JFH: Not immediately needed. Leaving out for now to reduce scope of work.
    consents: IVisitConsentModel[];
    needsProactiveFleaTickPrescription: boolean | undefined;
    appointmentType: IAppointmentTypeModel | undefined;
    notes: string | undefined;
    dateCreated: string;
    dateUpdated: string;
    visitAttachments: IVisitAttachmentModel[];
    visitSectionVersions: IVisitSectionVersionModel[];
    labOrders: ILabOrderModel[];
    anesthesiaForms: IVisitAnesthesiaFormModel[];
    hospitalPrescriptions: IHospitalPrescriptionModel[];
    isInAdminMode: boolean;
    followUpBookingURL: string | undefined;
}

export class VisitModel extends Model implements Record<keyof IVisitModel, any>  {
    id: number; // for json-server
    visitId: number;
    visitPublicId: string | undefined;
    cashPayment: number;
    checkPayment: number;
    clinicId: number;
    clinicStoreId: number;
    consultationValidationStatusId: number;
    consultationHasBeenValidated: boolean;
    visitDate: DateTime | undefined;
    fleaTickProductId: number | undefined;
    hasMicrochip: boolean | undefined;
    hasOptedOutOfPackage: boolean;
    hasOutsideContact: boolean | undefined;
    heartwormProductId: number | undefined;
    isExpress: boolean;
    isRxOnly: boolean;
    isPetNotPresent: boolean | undefined;
    isPetPregnant: boolean | undefined;
    lastVaccineDate: DateTime | undefined;
    lastRabiesDate: DateTime | undefined;
    paymentMethodId: number | undefined;
    petId: number;
    veterinarianSignaturePhotoId: number | undefined;
    vetServicesHasBeenValidated: boolean;
    vetServicesValidationStatusId: number;
    visitCompletionTypeId: number;
    visitStatusId: number;
    visitSummaryDocumentId: number | undefined;
    sMSStatus: number;
    hasVetAttestation: boolean;
    hasPetAttestation: boolean;
    room: string | undefined;
    visitWellnessExam: VisitWellnessExamModel;
    visitDiagnoses: VisitDiagnosisModel[];
    reasonForVisit: string | undefined;
    visitHistory: string;
    visitSubjective: string;
    normalResponses: string;
    abnormalResponses: string;
    unableToExamResponses: string;
    additionalInfoLabels: string[];
    showRabiesDisclaimer: boolean;
    vetAssistantFirstLast: string;
    brandId: number;
    clinicStateId: number;
    clinicAllowChangingSingleVisitVet: boolean;
    marketId: number;
    firstVisitDate: DateTime | undefined;
    lastVisitDate: DateTime | undefined;
    checkinStart: DateTime | undefined;
    consultationStart: DateTime | undefined;
    vetServicesStart: DateTime | undefined;
    checkoutStart: DateTime | undefined;
    clientSignaturePhotoId: number | undefined;
    rabiesCertificateDocumentId: number | undefined;
    prescriptionDocumentId: number | undefined;
    partnerId: number;
    visitStatus: VisitStatusModel;
    pet: PetModel;
    client: ClientModel;
    visitEvent: VisitEventModel;
    historicalVisitWellnessExams: VisitWellnessExamModel[];
    visitSalesItems: VisitSalesItemModel[];
    veterinarianSignaturePhoto: PhotoModel | undefined;
    clinicVetSignaturePhoto: PhotoModel | undefined;
    clientSignaturePhoto: PhotoModel | undefined;
    visitSummaryDocument: StorageContentModel | undefined;
    rabiesCertificateDocument: StorageContentModel | undefined;
    prescriptionDocument: StorageContentModel | undefined;
    salesPackageId: number | undefined;
    salesPackagePrice: number | undefined;
    salesPackageName: string | undefined;
    salesPackageDiscountAmount: number | undefined;
    salesPackageInvoicePrice: number | undefined;
    salesPackageSalesSubCategoryId: number | undefined;
    salesPackageSalesSubCategoryName: string;
    salesPackageSpeciesId: number | undefined;
    salesPackageSpeciesName: string;
    salesPackageDisplayName: string;
    salesPackageDescription: string;
    salesPackageCode: string;
    salesPackageHelpPhotoId: number | undefined;
    salesPackageSortOrder: number | undefined;
    salesPackageExpirationDays: number | undefined;
    salesPackageVaccineReminderDefaultDays: number | undefined;
    isSalesPackageRefunded: boolean;
    visitCompletionType: VisitCompletionTypeModel;
    serviceAmount: number;
    productAmount: number;
    licenseFeeAmount: number;
    vetcoPrescriptionAmount: number;
    discountAmount: number;
    taxPercentage: number;
    subtotalPreDiscountsAmount: number;
    subtotalPostDiscountsAmount: number;
    taxAmount: number;
    refundAmount: number;
    taxableTotalAmount: number;
    nonTaxableTotalAmount: number;
    totalAmount: number;
    servicesOnlyDiscountAmount: number;
    productsOnlyDiscountAmount: number;
    servicesRebateAmount: number | undefined;
    productsRebateAmount: number | undefined;
    totalRebateAmount: number | undefined;
    nonTaxableFeeAmount: number | undefined;
    taxableFeeAmount: number | undefined;
    totalFeeAmount: number | undefined;
    totalAOVAmount: number | undefined;
    veterinarianId: number | undefined;
    veterinarianFirstName: string;
    veterinarianLastName: string;
    veterinarianLicenseNumber: string;
    veterinarianLicenseStateId: number | undefined;
    veterinarianLicenseStateAbbreviation: string;
    veterinarianFullName: string;
    veterinarianLicenseExpirationDate: DateTime | undefined;
    veterinarianEmail: string;
    dispensingAuthUserId: number | undefined;
    dispensingUserFirstName: string | undefined;
    dispensingUserLastName: string | undefined;
    thankYouNote: string;
    storeStateNote: string;
    clientStateNote: string;
    visitTagId: number | undefined;
    reservationId: number | undefined;
    reservation: ReservationModel | undefined;
    examResults: VisitExamResultModel[];
    visitNotes: VisitNoteModel[] | undefined;
    hasEvaluatedAutoRecommendations: boolean;
    intakeId: number | undefined;
    intake: IntakeModel;
    arrived: DateTime | undefined;
    firstCheckIn: DateTime | undefined;
    visitStarted: DateTime | undefined;
    arrivedWaitingTime: number | undefined;
    checkInWaitingTime: number | undefined;
    intakeCompletionStatusId: number | undefined;
    needsProactiveFleaTickPrescription: boolean | undefined;
    appointmentType: AppointmentTypeModel | undefined;
    notes: string | undefined;
    dateCreated: DateTime;
    dateUpdated: DateTime;
    visitAttachments: VisitAttachmentModel[];
    consents: VisitConsentModel[];
    visitSectionVersions: VisitSectionVersionModel[];
    labOrders: LabOrderModel[];
    anesthesiaForms: VisitAnesthesiaFormModel[];
    hospitalPrescriptions: HospitalPrescriptionModel[];
    isInAdminMode: boolean;
    freeNonPackageSalesItems: VisitSalesItemModel[];
    addOnSalesItems: VisitSalesItemModel[];
    paidNonPackageSalesItems: VisitSalesItemModel[];
    discountAndRebateSalesItems: VisitSalesItemModel[];
    referrals: VisitReferralModel[];
    followUpBookingURL: string | undefined;
    constructor(params: IVisitModel) {
        super(params);

        this.visitId = params.visitId ?? 0
        this.visitPublicId = StringUtility.SanitizeString(params.visitPublicId)
        this.cashPayment = params.cashPayment ?? 0
        this.checkPayment = params.checkPayment ?? 0
        this.clinicId = params.clinicId ?? 0
        this.clinicStoreId = params.clinicStoreId ?? 0
        this.consultationHasBeenValidated = params.consultationHasBeenValidated ?? false
        this.consultationValidationStatusId = params.consultationValidationStatusId ?? 0
        this.visitDate = StringUtility.SanitizeDateTimeISOAsUtc(params.visitDate)
        this.fleaTickProductId = StringUtility.SanitizeNumber(params.fleaTickProductId)
        this.hasMicrochip = params.hasMicrochip ?? false
        this.hasOptedOutOfPackage = params.hasOptedOutOfPackage ?? false
        this.hasOutsideContact = StringUtility.SanitizeBool(params.hasOutsideContact)
        this.heartwormProductId = StringUtility.SanitizeNumber(params.heartwormProductId)
        this.isExpress = params.isExpress ?? false
        this.isRxOnly = params.isRxOnly ?? false
        this.isPetNotPresent = StringUtility.SanitizeBool(params.isPetNotPresent)
        this.isPetPregnant = StringUtility.SanitizeBool(params.isPetPregnant)
        this.lastVaccineDate = StringUtility.SanitizeDateTimeISO(params.lastVaccineDate)
        this.lastRabiesDate = StringUtility.SanitizeDateTimeISO(params.lastRabiesDate)
        this.paymentMethodId = StringUtility.SanitizeNumber(params.paymentMethodId)
        this.petId = params.petId ?? 0
        this.veterinarianSignaturePhotoId = StringUtility.SanitizeNumber(params.veterinarianSignaturePhotoId)
        this.vetServicesHasBeenValidated = params.vetServicesHasBeenValidated ?? false
        this.vetServicesValidationStatusId = params.vetServicesValidationStatusId ?? 0
        this.visitCompletionTypeId = params.visitCompletionTypeId ?? 0
        this.visitStatusId = params.visitStatusId ?? 0
        this.sMSStatus = params.sMSStatus ?? 0
        this.hasVetAttestation = params.hasVetAttestation ?? false
        this.hasPetAttestation = params.hasPetAttestation ?? false
        this.room = StringUtility.SanitizeString(params.room)
        this.visitWellnessExam = new VisitWellnessExamModel(params.visitWellnessExam)
        this.visitDiagnoses = (params.visitDiagnoses ?? []).map(vd => new VisitDiagnosisModel(vd))
        this.reasonForVisit = StringUtility.SanitizeString(params.reasonForVisit)
        this.notes = StringUtility.SanitizeString(params.notes)

        this.visitHistory = params.visitHistory
        this.visitSubjective = params.visitSubjective
        this.normalResponses = params.normalResponses
        this.abnormalResponses = params.abnormalResponses
        this.unableToExamResponses = params.unableToExamResponses
        this.additionalInfoLabels = params.additionalInfoLabels
        this.showRabiesDisclaimer = params.showRabiesDisclaimer
        this.vetAssistantFirstLast = params.vetAssistantFirstLast
        this.id = params.visitId
        this.brandId = params.brandId
        this.clinicStateId = params.clinicStateId
        this.clinicAllowChangingSingleVisitVet = params.clinicAllowChangingSingleVisitVet
        this.marketId = params.marketId

        // vist start event times
        this.firstVisitDate = StringUtility.SanitizeDateTimeISO(params.firstVisitDate)
        this.lastVisitDate = StringUtility.SanitizeDateTimeISO(params.lastVisitDate)
        this.checkinStart = StringUtility.SanitizeDateTimeISO(params.checkinStart)
        this.consultationStart = StringUtility.SanitizeDateTimeISO(params.consultationStart)
        this.vetServicesStart = StringUtility.SanitizeDateTimeISO(params.vetServicesStart)
        this.checkoutStart = StringUtility.SanitizeDateTimeISO(params.checkoutStart)

        this.rabiesCertificateDocumentId = StringUtility.SanitizeNumber(params.rabiesCertificateDocumentId)
        this.prescriptionDocumentId = StringUtility.SanitizeNumber(params.prescriptionDocumentId)
        this.partnerId = params.partnerId
        this.visitStatus = new VisitStatusModel(params.visitStatus)
        this.pet = new PetModel(params.pet)
        this.client = new ClientModel(params.client)
        this.visitEvent = new VisitEventModel(params.visitEvent)
        this.historicalVisitWellnessExams = (params.historicalVisitWellnessExams ?? []).map(e => new VisitWellnessExamModel(e));
        this.visitSalesItems = (params.visitSalesItems ?? []).map(si => new VisitSalesItemModel(si, this.visitDate));
        this.veterinarianSignaturePhoto = params.veterinarianSignaturePhoto ? new PhotoModel(params.veterinarianSignaturePhoto) : undefined
        this.clinicVetSignaturePhoto = params.clinicVetSignaturePhoto ? new PhotoModel(params.clinicVetSignaturePhoto) : undefined
        this.clientSignaturePhoto = params.clientSignaturePhoto ? new PhotoModel(params.clientSignaturePhoto) : undefined
        this.visitSummaryDocument = params.visitSummaryDocument ? new StorageContentModel(params.visitSummaryDocument) : undefined
        this.rabiesCertificateDocument = params.rabiesCertificateDocument ? new StorageContentModel(params.rabiesCertificateDocument) : undefined
        this.prescriptionDocument = params.prescriptionDocument ? new StorageContentModel(params.prescriptionDocument) : undefined
        this.salesPackageId = StringUtility.SanitizeNumber(params.salesPackageId)
        this.salesPackagePrice = StringUtility.SanitizeNumber(params.salesPackagePrice)
        this.salesPackageDiscountAmount = params.salesPackageDiscountAmount
        this.salesPackageInvoicePrice = params.salesPackageInvoicePrice;
        this.salesPackageName = params.salesPackageName
        this.salesPackageSalesSubCategoryId = StringUtility.SanitizeNumber(params.salesPackageSalesSubCategoryId)
        this.salesPackageSalesSubCategoryName = params.salesPackageSalesSubCategoryName
        this.salesPackageSpeciesId = StringUtility.SanitizeNumber(params.salesPackageSpeciesId)
        this.salesPackageSpeciesName = params.salesPackageSpeciesName
        this.salesPackageDisplayName = params.salesPackageDisplayName
        this.salesPackageDescription = params.salesPackageDescription
        this.salesPackageCode = params.salesPackageCode
        this.salesPackageHelpPhotoId = StringUtility.SanitizeNumber(params.salesPackageHelpPhotoId)
        this.salesPackageSortOrder = StringUtility.SanitizeNumber(params.salesPackageSortOrder)
        this.salesPackageExpirationDays = StringUtility.SanitizeNumber(params.salesPackageExpirationDays)
        this.salesPackageVaccineReminderDefaultDays = StringUtility.SanitizeNumber(params.salesPackageVaccineReminderDefaultDays)
        this.isSalesPackageRefunded = params.isSalesPackageRefunded
        this.visitCompletionType = new VisitCompletionTypeModel(params.visitCompletionType)
        this.serviceAmount = params.serviceAmount
        this.productAmount = params.productAmount
        this.licenseFeeAmount = params.licenseFeeAmount
        this.vetcoPrescriptionAmount = params.vetcoPrescriptionAmount
        this.discountAmount = params.discountAmount
        this.taxPercentage = params.taxPercentage
        this.subtotalPreDiscountsAmount = params.subtotalPreDiscountsAmount
        this.subtotalPostDiscountsAmount = params.subtotalPostDiscountsAmount
        this.taxAmount = params.taxAmount
        this.refundAmount = params.refundAmount
        this.taxableTotalAmount = params.taxableTotalAmount
        this.nonTaxableTotalAmount = params.nonTaxableTotalAmount
        this.totalAmount = params.totalAmount
        this.servicesOnlyDiscountAmount = params.servicesOnlyDiscountAmount
        this.productsOnlyDiscountAmount = params.productsOnlyDiscountAmount
        this.servicesRebateAmount = StringUtility.SanitizeNumber(params.servicesRebateAmount)
        this.productsRebateAmount = StringUtility.SanitizeNumber(params.productsRebateAmount)
        this.totalRebateAmount = StringUtility.SanitizeNumber(params.totalRebateAmount)
        this.nonTaxableFeeAmount = StringUtility.SanitizeNumber(params.nonTaxableFeeAmount)
        this.taxableFeeAmount = StringUtility.SanitizeNumber(params.taxableFeeAmount)
        this.totalFeeAmount = StringUtility.SanitizeNumber(params.totalFeeAmount)
        this.totalAOVAmount = StringUtility.SanitizeNumber(params.totalAOVAmount)
        this.veterinarianId = StringUtility.SanitizeNumber(params.veterinarianId)
        this.veterinarianFirstName = params.veterinarianFirstName
        this.veterinarianLastName = params.veterinarianLastName
        this.veterinarianLicenseNumber = params.veterinarianLicenseNumber
        this.veterinarianLicenseStateId = StringUtility.SanitizeNumber(params.veterinarianLicenseStateId)
        this.veterinarianLicenseStateAbbreviation = params.veterinarianLicenseStateAbbreviation
        this.veterinarianFullName = params.veterinarianFullName
        this.veterinarianLicenseExpirationDate = StringUtility.SanitizeDateTimeISO(params.veterinarianLicenseExpirationDate)
        this.veterinarianEmail = params.veterinarianEmail
        this.dispensingAuthUserId = StringUtility.SanitizeNumber(params.dispensingAuthUserId)
        this.dispensingUserFirstName = StringUtility.SanitizeString(params.dispensingUserFirstName)
        this.dispensingUserLastName = StringUtility.SanitizeString(params.dispensingUserLastName)
        this.thankYouNote = params.thankYouNote
        this.storeStateNote = params.storeStateNote
        this.clientStateNote = params.clientStateNote
        this.visitTagId = StringUtility.SanitizeNumber(params.visitTagId)
        this.reservationId = StringUtility.SanitizeNumber(params.reservationId)
        this.reservation = params.reservation ? new ReservationModel(params.reservation) : undefined;
        this.examResults = (params.examResults ?? []).map(e => new VisitExamResultModel(e));
        this.visitNotes = (params.visitNotes ?? []).map(n => new VisitNoteModel(n))
        this.hasEvaluatedAutoRecommendations = params.hasEvaluatedAutoRecommendations
        this.intakeId = StringUtility.SanitizeNumber(params.intakeId)
        this.intake = new IntakeModel(params.intake)
        this.arrived = StringUtility.SanitizeDateTimeISO(params.arrived)
        this.firstCheckIn = StringUtility.SanitizeDateTimeISO(params.firstCheckIn)
        this.visitStarted = StringUtility.SanitizeDateTimeISO(params.visitStarted)
        this.arrivedWaitingTime = StringUtility.SanitizeNumber(params.arrivedWaitingTime)
        this.checkInWaitingTime = StringUtility.SanitizeNumber(params.checkInWaitingTime)
        this.intakeCompletionStatusId = StringUtility.SanitizeNumber(params.intakeCompletionStatusId)
        this.needsProactiveFleaTickPrescription = StringUtility.SanitizeBool(params.needsProactiveFleaTickPrescription)
        this.appointmentType = params.appointmentType ? new AppointmentTypeModel(params.appointmentType) : undefined;
        this.reasonForVisit = StringUtility.SanitizeString(params.reasonForVisit);
        this.notes = StringUtility.SanitizeString(params.notes);
        this.dateCreated = DateTime.fromISO(params.dateCreated, { zone: "utc" });
        this.dateUpdated = DateTime.fromISO(params.dateUpdated, { zone: "utc" });
        this.visitAttachments = (params.visitAttachments ?? []).map(v => new VisitAttachmentModel(v));
        this.consents = (params.consents ?? []).map(c => new VisitConsentModel(c));
        this.visitSectionVersions = (params.visitSectionVersions ?? []).map(vsv => new VisitSectionVersionModel(vsv));
        this.labOrders = (params.labOrders ?? []).map(lo => new LabOrderModel(lo));
        this.anesthesiaForms = (params.anesthesiaForms ?? []).map(af => new VisitAnesthesiaFormModel(af));
        this.hospitalPrescriptions = (params.hospitalPrescriptions ?? []).map(hp => new HospitalPrescriptionModel(hp));
        this.isInAdminMode = params.isInAdminMode;
        this.freeNonPackageSalesItems = (params.freeNonPackageSalesItems ?? []).map(si => new VisitSalesItemModel(si, this.visitDate));
        this.addOnSalesItems = (params.addOnSalesItems ?? []).map(si => new VisitSalesItemModel(si, this.visitDate));
        this.paidNonPackageSalesItems = (params.paidNonPackageSalesItems ?? []).map(si => new VisitSalesItemModel(si, this.visitDate));
        this.discountAndRebateSalesItems = (params.discountAndRebateSalesItems ?? []).map(si => new VisitSalesItemModel(si, this.visitDate));
        this.referrals = (params.referrals ?? []).map(rf => new VisitReferralModel(rf));
        this.followUpBookingURL=params.followUpBookingURL;
    }
 

    filterVisitNotesByVisitNoteType = (visitNoteType: VisitNoteType | undefined): VisitNoteModel[] | undefined => {
        return this.visitNotes?.filter((v) => v.visitNoteTypeId == visitNoteType);
    }

    hasVisitSummary = () =>
        this.visitSalesItems.some(vsi => vsi.salesItemCommunicationTypeId === CommunicationType.VisitSummary);

    hasPrescription = () =>
        this.visitSalesItems.some(vsi => vsi.salesItemCommunicationTypeId === CommunicationType.Prescription);

    hasRabiesCertificate = () =>
        this.visitSalesItems.some(vsi => vsi.salesItemCommunicationTypeId === CommunicationType.RabiesCertificate);

    hasDischargeInstructions = () =>
        this.visitWellnessExam.dischargeNotes !== undefined || (this.visitNotes ?? []).some(vn => vn.visitNoteTypeId === VisitNoteType.Discharge);

    hasAnesthesiaMonitoring = () =>
        this.visitSalesItems.some(vsi => vsi.salesItemCommunicationTypeId === CommunicationType.AnesthesiaMonitoring);

    isVetTechExam = () => {
        return ReservationAssignmentStatuses.Assigned_Technician === this.reservation?.reservationAssignmentStatusId;
    }

    hasHospitalPrescriptions = () => this.hospitalPrescriptions.length > 0 || this.isRxOnly === true

    getFollowUpAppointmentInfo = () =>{
        return new FollowUpAppointmentModel({
            followUpBookingURL:this.followUpBookingURL!,
            clinetFirstname:this.client?.firstName!,
            clinetLastName:this.client?.lastName!,
            clinetCellPhone:this.client?.cellPhone!,
            clinetEmailAddress:this.client?.emailAddress!,
            petname:this.pet?.name!,
            petspecies:this.pet?.species?.name!
            ,store_number:"",
            clinicZipCode:"",
            isreturningcustomer:"true"
        })
    }
}
